/**
 * Helper class for dealing with timestamp strings according to ISO 8601.
 */

export const TimeStampHelper = {

  /**
   * Removes the seconds from the timestamp.
   * example: '2011-10-05T14:48:00.000Z' --> '2011-10-05T14:48'
   *
   * @param {String} isoString A timestamp string according to ISO 8601.
   */
  removeSeconds: function (isoString) {
    return isoString.substring(0, 16);
  },

  /**
   * Extracts date from timestamp.
   * example: '2011-10-05T14:48:00.000Z' --> '2011-10-05'
   *
   * @param {String} isoString A timestamp string according to ISO 8601.
   */
  extractDate: function (isoString) {
    return isoString.substring(0, 10);
  },

  /**
   * Extracts year from timestamp.
   * example: '2011-10-05T14:48:00.000Z' --> '2011'
   *
   * @param {String} isoString A timestamp string according to ISO 8601.
   */
  extractYear: function (isoString) {
    return isoString.substring(0, 4);
  },

  /**
   * Extracts the hours and minutes from the ISO timestamp.
   * example: '2011-10-05T14:48:00.000Z' --> '14:48'
   *
   * @param {String} isoString A timestamp string according to ISO 8601.
   */
  extractHoursAndMinutes: function (isoString) {
    return isoString.substring(11, 16);
  },

  // TODO: this function is currently not used, but might
  //       contain interesting code snippets for later
  /**
   * Adapt current timestamp to next valid value.
   *
   * Periodicity can be specified like described here:
   * https://docs.geoserver.org/latest/en/user/services/wms/time.html#specifying-a-periodicity
   * This function currently only uses hours and minutes.
   * The starting point of the time intervall is assumed to be midnight (00:00).
   *
   * @param {Date} timeStamp The current timestamp of the application.
   * @param {String} periodicity The periodicity of the WMS-T.
   * @returns {date} A valid timestamp according to periodicity.
   */
  periodicityAdaptedTimeStamp: function (timeStamp, periodicity) {
    if (!periodicity) {
      return;
    }

    // parse the values from duration string
    // regex taken from https://rgxdb.com/r/5XR5V1JW
    const regex = /^(-?)P(?=\d|T\d)(?:(\d+)Y)?(?:(\d+)M)?(?:(\d+)([DW]))?(?:T(?:(\d+)H)?(?:(\d+)M)?(?:(\d+(?:\.\d+)?)S)?)?$/;
    const matches = periodicity.match(regex);

    // not needed now, but useful for a later extension
    const yearsResult = matches[2];
    const monthsResults = matches[3];
    const daysResult = matches[4];

    const hoursResult = matches[6];
    const minutesResult = matches[7];

    // compute the duration of the periodicity in milliseconds
    let p = new Date(0);

    let years = parseInt(yearsResult);

    if (years) {
      p.setUTCFullYear(
        p.getUTCFullYear() + years
      );
    }

    let months = parseInt(monthsResults);
    if (months) {
      p.setUTCMonth(
        p.getUTCMonth() + months
      );
    }

    let days = parseInt(daysResult);
    if (days) {
      p.setUTCDate(
        p.getUTCDate() + days
      );
    }

    let hours = parseInt(hoursResult);
    if (hours) {
      p.setUTCHours(
        p.getUTCHours() + hours
      );
    }

    let minutes = parseInt(minutesResult);
    if (!minutes) {
      minutes = 0;
    }
    p.setUTCMinutes(
      p.getUTCMinutes() + minutes
    );

    let elapsedMs = new Date(timeStamp).getTime();

    // TODO: does not work for years after 1971, maybe "Schaltjahr" is the problem ... (?)
    // check if the elapsed time is valid for the periodicity
    // if not, it will be adjusted to the last valid time
    const differenceToValidTimeStamp = elapsedMs % p;

    let result = timeStamp;

    if (differenceToValidTimeStamp !== 0) {
      // create valid timeStamp by subtracting
      // difference milli seconds
      result = new Date(timeStamp - differenceToValidTimeStamp);
    }
    return result;
  },

  /**
     * Adapt the timestamp for using as WMS time parameter.
     *
     * @param {Date} timeStamp The given timestamp
     * @param {String} timeFrequency The timeFrequency
     * @param {Number} timeExpectedDateDigits Expected number of digits
     *
     * @returns {Date} The adapted timestamp
     */
  adaptTimeStampForWms (timeStamp, timeFrequency, timeExpectedDateDigits) {
    // snap timestamp to valid value
    const timeStampForWmst =
            TimeStampHelper.adaptStampToIntervall(
              timeStamp,
              timeFrequency
            )

    // bring timestamp to required format
    let formattedTimeStamp = timeStampForWmst.toISOString();

    if (timeExpectedDateDigits) {
      formattedTimeStamp = formattedTimeStamp.substring(
        0,
        timeExpectedDateDigits
      );
    }

    return formattedTimeStamp;
  },

  /**
   * Adapt timestamp to intervall.
   *
   * Only handles common cases. In case no intervall
   * type matches, the original timestamp is returned.
   *
   * @param {Date} timeStamp The given timestamp.
   * @param {String} intervall The intervall.
   * @returns {Date} The adapted timestamp.
   */
  adaptStampToIntervall: function (timeStamp, intervall) {
    if (!timeStamp) {
      return;
    }
    let result = timeStamp;
    switch (intervall) {
      case 'yearly':
      case 'P1Y':
        // e.g. 2021-02-14T12:43:65.252Z
        //  --> 2021-01-01T00:00:00.000Z
        const year = timeStamp.getUTCFullYear();

        result = new Date(0);
        result.setUTCFullYear(year);
        break;
      case 'daily':
      case 'PT1D':
        result.setUTCHours(0, 0, 0, 0);
        // e.g. 2021-02-14T12:43:65.252Z
        //  --> 2021-02-14T00:00:00.000Z
        break
      case 'hourly':
      case 'PT1H':
        result.setUTCMinutes(0, 0, 0);
        // e.g. 2021-02-14T12:43:65.252Z
        //  --> 2021-02-14T12:00:00.000Z
        break
    }

    return result;
  },

  /**
   * Creates the initial shared timestamp
   *
   * Checks if the provided timestamp is valid.
   * If yes, it is taken as the start time of the application.
   * Otherwise the current moment is taken.
   *
   * @param {String} dateTimeStr ISO date string or undefined to use "now".
   */
  initSharedTimestamp (dateTimeStr) {
    let timeStamp;
    let timeStampValid = false;

    // TODO: does not work on Firefox
    // checks if input timestring is valid
    if (dateTimeStr) {
      // check if timestamp already ends with 'Z'
      const utcIndicator = 'Z';
      if (!dateTimeStr.endsWith(utcIndicator)) {
        // add 'Z' in order to indicate UTC time
        dateTimeStr = dateTimeStr + utcIndicator;
      }
      timeStamp = new Date(dateTimeStr);
      timeStampValid = timeStamp instanceof Date && !isNaN(timeStamp);
      // inspired by https://stackoverflow.com/a/1353711
    }

    // create current timestamp in case input timestamp is invalid
    if (!timeStampValid) {
      timeStamp = new Date()
    }

    // Jump to last 5 minute time step
    const periodicity = 5;
    const currentMinutes = timeStamp.getUTCMinutes();
    const newMinutes = Math.floor(currentMinutes / periodicity) * periodicity;

    timeStamp.setUTCMinutes(newMinutes);

    // Only full minutes are required
    timeStamp.setUTCSeconds(0);
    timeStamp.setUTCMilliseconds(0);

    return timeStamp;
  }
}



// WEBPACK FOOTER //
// ./app/util/TimeStamp.js